'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Tag } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { useBrandSelections } from '../../hooks/use-brand-selections';
import { useBrandsNavigation } from '../../hooks/use-brands-navigation';
import { BrandsContent } from '../settings-content/BrandsContent';
import { BrandSelectionSummary } from '../settings-content/BrandSelectionSummary';
import { SettingsSectionRow } from './SettingsSection';

interface BrandsSectionProps {
  selections: string[] | null;
  onSelectionsChange: (selections: string[] | null) => void;
  hasChanges?: boolean;
}

/**
 * Brands — the settings row for real manufacturer products.
 *
 * Its own root row rather than a tab inside Colors: siding is a wall covering
 * with a material and a geometry, not a colour, and the list of libraries
 * behind this row keeps growing. Everything below it is one navigator — see
 * {@link BrandsContent}.
 *
 * The summary is the last link in the propagation chain the picker builds:
 * every level shows what is picked beneath it, and this row shows the lot —
 * one product per line, wrapping, with a real swatch where the manufacturer
 * publishes a colour value. It sits BELOW the title rather than in the trailing
 * slot, because a trailing value shares the title's line and truncates, and a
 * truncated product name is the one thing this row exists to avoid.
 */
export function BrandsSection({
  selections,
  onSelectionsChange,
  hasChanges = false,
}: BrandsSectionProps) {
  const t = useTranslations('render.settings.brands');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // Labels come resolved from the API so the summary reads the same here, on
  // Apple and on Android — no client ever concatenates a product name.
  const { entries } = useBrandSelections(selections);
  // Owned here, not inside the content: the sheet's single Back chevron walks UP
  // the tree and only closes the sheet from the top, so the picker needs no back
  // control of its own. Two of them (one leaving, one going up a level) is what
  // this replaces.
  const nav = useBrandsNavigation();

  // Nothing picked reads "Auto", like every other optional setting: a blank
  // Brands row is a legitimate final state, and "Not selected" made it read as
  // an unfinished form field.
  //
  // Read off the stored selection, not off the resolved entries: while the
  // lookup is in flight `entries` is empty, and a row that says "Auto" for a
  // building that has products chosen is a lie the user has no way to
  // question. Blank for a moment, then the full list.
  const hasPicks = (selections?.length ?? 0) > 0;

  return (
    <>
      <SettingsSectionRow
        icon={Tag}
        title={t('title')}
        // Nothing picked is one short word and belongs on the title's line;
        // a real selection gets the full width below it.
        subtitle={hasPicks ? undefined : t('auto')}
        stackedContent={
          hasPicks ? (
            <BrandSelectionSummary entries={entries} showSwatches />
          ) : undefined
        }
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          // Reopening lands on the list of libraries, not three screens deep
          // inside whatever was open last time.
          if (!open) nav.goToRoot();
        }}
      >
        <NestedSheetContent>
          <NestedSheetHeader onBack={nav.goBack}>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <BrandsContent
              selections={selections}
              onSelectionsChange={onSelectionsChange}
              nav={nav}
              variant="compact"
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
